// webapp/src/lib/sentryFeedbackCopy.ts
//
// Libellés du widget de retour Sentry (`feedbackIntegration`), dans les deux
// langues. Phase 98, D-04.
//
// POURQUOI ICI ET PAS DANS LE CATALOGUE i18n (`i18n/locales/{fr,en}.ts`) —
// deux raisons, la première structurelle, la seconde mesurée :
//
//  1. Ces libellés sont consommés HORS React, par `initSentry()` qui tourne
//     dans `main.tsx` AVANT le montage de `<LanguageProvider>`. Le catalogue
//     ne s'atteint que par `useLanguage()`, un hook. Les y mettre obligerait à
//     importer les deux catalogues entiers dans le bundle d'amorçage pour n'en
//     tirer que douze chaînes.
//  2. Le cas `E4` de `i18n/catalogue.test.ts` mesure le catalogue français
//     contre un seuil de découpage de 30 000 octets. MESURÉ le 2026-08-19 en
//     forçant l'échec de E4 : « charge utile fr : 29427 B bruts / 445 clés »,
//     soit 573 octets de marge — ~9 clés au coût moyen constaté de ~66 o/clé.
//     Les DOUZE libellés ci-dessous pèsent 453 octets en JSON brut : ils
//     PASSERAIENT tout juste sous clés nues (120 o restants), et feraient
//     céder E4 dès qu'on les nommerait comme le catalogue le fait, par espace
//     (`sentryFeedback.triggerLabel`, +16 o/clé → 645 o). C'est la mesure du
//     jour, pas la reprise du plan, qui annonçait un dépassement sec.
//     Autrement dit : la raison n°1 suffit à elle seule, celle-ci ne fait
//     qu'ôter la dernière tentation. Le découpage du catalogue n'est pas le
//     sujet de cette phase.
//
// La copie française est VERBATIM celle qui était en dur dans `sentry.ts`
// jusqu'à ce plan — caractère pour caractère.
//
// Refs: phase 98

import { langFromPath } from '../i18n/urlLang'

/** Les douze libellés que `Sentry.feedbackIntegration` accepte et qu'on pose. */
export interface FeedbackCopy {
  /**
   * Libellé du bouton persistant. NOM CRITIQUE : l'acteur Sentry lit
   * `triggerAriaLabel || triggerLabel || TRIGGER_LABEL`. Cette clé s'est
   * d'abord appelée `buttonLabel` — le nom de l'option en v7 — qui n'existe
   * dans aucun paquet @sentry installé depuis la v8. Sentry l'ignorait en
   * silence et affichait son défaut anglais « Report a Bug », y compris sur les
   * pages françaises. Renommer, ne pas dupliquer : garder les deux ferait
   * croire à une ceinture-bretelles là où l'une des deux n'est pas lue.
   */
  triggerLabel: string
  formTitle: string
  submitButtonLabel: string
  cancelButtonLabel: string
  nameLabel: string
  namePlaceholder: string
  emailLabel: string
  emailPlaceholder: string
  messageLabel: string
  messagePlaceholder: string
  successMessageText: string
  addScreenshotButtonLabel: string
  /**
   * Les trois qui suivent ont été AJOUTÉES le 2026-08-31, après la phase 98.
   * Elles ne manquaient pas par choix : personne n'avait comparé les clés
   * fournies à celles que Sentry sait traduire, donc `Confirm`,
   * `(required)` et `Remove screenshot` restaient en anglais au milieu du
   * formulaire français. Elles sont peu visibles — il faut ouvrir le
   * formulaire, et pour deux d'entre elles ajouter une capture — ce qui
   * explique qu'elles aient survécu à la relecture de la phase 98.
   */
  confirmButtonLabel: string
  isRequiredLabel: string
  removeScreenshotButtonLabel: string
}

export const FEEDBACK_COPY: Record<'fr' | 'en', FeedbackCopy> = {
  fr: {
    triggerLabel: 'Un problème ?',
    formTitle: 'Signaler un problème',
    submitButtonLabel: 'Envoyer',
    cancelButtonLabel: 'Annuler',
    nameLabel: 'Nom',
    namePlaceholder: 'Votre nom (optionnel)',
    emailLabel: 'Email',
    emailPlaceholder: 'Votre email (pour vous répondre)',
    messageLabel: 'Description',
    messagePlaceholder: 'Décrivez le problème rencontré',
    successMessageText: 'Merci, votre retour a bien été envoyé.',
    addScreenshotButtonLabel: 'Ajouter une capture',
    confirmButtonLabel: 'Confirmer',
    isRequiredLabel: '(obligatoire)',
    removeScreenshotButtonLabel: 'Retirer la capture',
  },
  en: {
    triggerLabel: 'Something wrong?',
    formTitle: 'Report a problem',
    submitButtonLabel: 'Send',
    cancelButtonLabel: 'Cancel',
    nameLabel: 'Name',
    namePlaceholder: 'Your name (optional)',
    emailLabel: 'Email',
    emailPlaceholder: 'Your email (so we can reply)',
    messageLabel: 'Description',
    messagePlaceholder: 'Describe the problem you ran into',
    successMessageText: 'Thanks, your feedback has been sent.',
    addScreenshotButtonLabel: 'Add a screenshot',
    confirmButtonLabel: 'Confirm',
    isRequiredLabel: '(required)',
    removeScreenshotButtonLabel: 'Remove screenshot',
  },
}

/**
 * Jeu de libellés pour un chemin donné. Fonction pure : l'appelant fournit
 * `window.location.pathname`, comme `AuthProvider` le fait déjà pour la même
 * raison (lecture de la langue hors React).
 *
 * Un chemin que `langFromPath` ne reconnaît pas rend le français — le
 * comportement d'aujourd'hui, inchangé.
 */
export function feedbackCopy(pathname: string): FeedbackCopy {
  return langFromPath(pathname) === 'en' ? FEEDBACK_COPY.en : FEEDBACK_COPY.fr
}
